import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { ArrowRight, Loader2, ShieldCheck, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { LogoMark } from '@/components/brand/Logo';
import { useBranding, useBrandLogoBlobUrl } from '@/hooks/useBranding';
import { cn } from '@/lib/utils';

const PhoneSchema = z.object({
  phone: z
    .string()
    .min(8, 'Phone is required')
    .regex(/^\+?[1-9]\d{6,14}$/, 'Use E.164 format, e.g. +918000002000')
});

const OtpSchema = z.object({
  otp: z.string().regex(/^\d{4,8}$/, '4–8 digit OTP required')
});

type Step = 'phone' | 'otp';

export function LoginPage(): JSX.Element {
  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [devOtp, setDevOtp] = useState<string | null>(null);
  const navigate = useNavigate();
  const { requestOtp, verifyOtp } = useAuth();
  // White-label branding — best-effort. The brand fetch only resolves when
  // there's still an active session (e.g. user landed here mid-flow). For
  // truly anonymous visitors we render the default HoodDesk brand.
  const branding = useBranding();
  const brandLogoBlobUrl = useBrandLogoBlobUrl(branding.brand_logo_url);

  const phoneForm = useForm<z.infer<typeof PhoneSchema>>({
    resolver: zodResolver(PhoneSchema),
    defaultValues: { phone: '' }
  });
  const otpForm = useForm<z.infer<typeof OtpSchema>>({
    resolver: zodResolver(OtpSchema),
    defaultValues: { otp: '' }
  });

  async function onRequest(values: z.infer<typeof PhoneSchema>): Promise<void> {
    try {
      const res = await requestOtp(values.phone);
      setPhone(values.phone);
      setStep('otp');
      // Dev-only autofill chip — gated by Vite DEV flag + opt-in query param
      // so production demos never leak the OTP on screen.
      const isDevMode = import.meta.env.DEV;
      const devChipOptIn =
        typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('dev') === '1';
      if (res.dev_otp && isDevMode && devChipOptIn) {
        setDevOtp(res.dev_otp);
        otpForm.setValue('otp', res.dev_otp);
        toast.success('OTP sent. Dev autofill available.');
      } else {
        toast.success(`OTP sent to ${values.phone}`);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to send OTP');
    }
  }

  async function onVerify(values: z.infer<typeof OtpSchema>): Promise<void> {
    try {
      const res = await verifyOtp(phone, values.otp);
      toast.success('Signed in');
      // Vendors land on their portal; everyone else gets the admin dashboard.
      const dest = res.user?.role === 'vendor' ? '/vendor-portal' : '/dashboard';
      void navigate({ to: dest });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Invalid OTP');
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-vt-bg p-vt-4">
      {/* Subtle brand gradient background */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_50%_at_50%_0%,rgba(13,59,102,0.10)_0%,transparent_60%),radial-gradient(40%_40%_at_100%_100%,rgba(44,89,133,0.10)_0%,transparent_60%)]"
      />
      {/* Soft grid overlay */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.05] [background-image:linear-gradient(to_right,#1a202c_1px,transparent_1px),linear-gradient(to_bottom,#1a202c_1px,transparent_1px)] [background-size:32px_32px]"
      />

      <div className="relative z-10 w-full max-w-md">
        <Card className="overflow-hidden shadow-vt-3 transition-shadow duration-vt-slow hover:shadow-vt-3">
          <CardHeader className="items-center text-center pb-vt-3">
            <div className="mx-auto mb-vt-3">
              {brandLogoBlobUrl ? (
                <img
                  src={brandLogoBlobUrl}
                  alt="Society logo"
                  className="h-14 w-14 rounded-vt-md object-contain drop-shadow-[0_8px_24px_rgba(13,59,102,0.25)]"
                />
              ) : (
                <LogoMark size={56} className="drop-shadow-[0_8px_24px_rgba(13,59,102,0.25)]" />
              )}
            </div>
            <CardTitle className="text-vt-24 tracking-[-0.01em]">Sign in to HoodDesk Admin</CardTitle>
            <CardDescription className="max-w-xs">
              {branding.brand_welcome_copy
                ? branding.brand_welcome_copy
                : step === 'phone'
                  ? 'Use the phone number registered with your society.'
                  : `We sent a one-time code to ${phone}.`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div
              key={step}
              className="animate-in fade-in-0 slide-in-from-bottom-2 duration-vt"
            >
              {step === 'phone' ? (
                <form onSubmit={phoneForm.handleSubmit(onRequest)} className="flex flex-col gap-vt-4">
                  <div className="flex flex-col gap-vt-2">
                    <Label htmlFor="phone">Phone number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      autoFocus
                      placeholder="+918000002000"
                      {...phoneForm.register('phone')}
                    />
                    {phoneForm.formState.errors.phone ? (
                      <p className="text-vt-12 text-vt-alert" role="alert">
                        {phoneForm.formState.errors.phone.message}
                      </p>
                    ) : (
                      <p className="text-vt-12 text-vt-text-soft">We will send a 6-digit code via SMS.</p>
                    )}
                  </div>
                  <Button
                    type="submit"
                    size="lg"
                    className="w-full"
                    disabled={phoneForm.formState.isSubmitting}
                  >
                    {phoneForm.formState.isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                      </>
                    ) : (
                      <>
                        Send OTP <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>
              ) : (
                <form onSubmit={otpForm.handleSubmit(onVerify)} className="flex flex-col gap-vt-4">
                  <div className="flex flex-col gap-vt-2">
                    <Label htmlFor="otp">One-time code</Label>
                    <Input
                      id="otp"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={8}
                      autoFocus
                      placeholder="••••••"
                      className="text-center text-vt-18 font-semibold tracking-[0.4em]"
                      {...otpForm.register('otp')}
                    />
                    {devOtp ? (
                      <DevAutofillChip
                        otp={devOtp}
                        onUse={() => otpForm.setValue('otp', devOtp, { shouldValidate: true })}
                      />
                    ) : null}
                    {otpForm.formState.errors.otp ? (
                      <p className="text-vt-12 text-vt-alert" role="alert">
                        {otpForm.formState.errors.otp.message}
                      </p>
                    ) : null}
                  </div>
                  <Button
                    type="submit"
                    size="lg"
                    className="w-full"
                    disabled={otpForm.formState.isSubmitting}
                  >
                    {otpForm.formState.isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> Verifying…
                      </>
                    ) : (
                      <>
                        Verify and continue <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setStep('phone');
                      setDevOtp(null);
                      otpForm.reset({ otp: '' });
                    }}
                  >
                    Use a different number
                  </Button>
                </form>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="mt-vt-4 flex items-center justify-center gap-vt-2 text-vt-12 text-vt-text-muted">
          <ShieldCheck className="h-3.5 w-3.5 text-vt-safe" strokeWidth={2} />
          <span>Encrypted in transit. Sessions expire after 30 minutes of inactivity.</span>
        </div>
      </div>
    </div>
  );
}

function DevAutofillChip({ otp, onUse }: { otp: string; onUse: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onUse}
      className={cn(
        'group inline-flex items-center justify-between gap-vt-2 rounded-vt-sm border border-dashed border-vt-info bg-vt-info-100/60 px-vt-3 py-vt-2',
        'text-vt-12 text-vt-info transition-colors duration-vt hover:bg-vt-info-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vt-info'
      )}
      aria-label={`Use development OTP ${otp}`}
    >
      <span className="flex items-center gap-vt-2">
        <Zap className="h-3.5 w-3.5" strokeWidth={2} />
        <span className="font-medium">Dev OTP available</span>
        <span className="font-mono">{otp}</span>
      </span>
      <span className="text-vt-12 font-semibold uppercase tracking-wider opacity-70 transition-opacity group-hover:opacity-100">
        Use
      </span>
    </button>
  );
}
